import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, Users, Building2, ListPlus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { PersonDialog } from "@/components/PersonDialog";
import { CompanyDialog } from "@/components/CompanyDialog";
import { ContactCard } from "@/components/ContactCard";
import { ContactDetailDialog } from "@/components/ContactDetailDialog";
import { DuplicatesPanel } from "@/components/DuplicatesPanel";
import { SubmissionsPanel, ShareIntakeLink } from "@/components/SubmissionsPanel";
import { DemoInboxPanel } from "@/components/DemoInboxPanel";

const ALL = "all";

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({ people: 0, companies: 0 });
  const [filters, setFilters] = useState({ roles: [], tags: [], locations: [], companies: [] });
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState(ALL);
  const [tag, setTag] = useState(ALL);
  const [listId, setListId] = useState(ALL);
  const [companyId, setCompanyId] = useState(ALL);
  const [personOpen, setPersonOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dupCount, setDupCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const setTab = (value) => {
    const next = new URLSearchParams(params);
    next.set("tab", value);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    const query = {};
    if (q.trim()) query.q = q.trim();
    if (tab === "people") query.kind = "person";
    if (tab === "companies") query.kind = "company";
    if (role !== ALL) query.role = role;
    if (tag !== ALL) query.tag = tag;
    if (listId !== ALL) query.list_id = listId;
    if (companyId !== ALL) query.company_id = companyId;
    const { data } = await api.get("/contacts", { params: query });
    setContacts(data.contacts);
    setCounts(data.counts);
  }, [q, tab, role, tag, listId, companyId]);

  const loadMeta = useCallback(async () => {
    const [f, l] = await Promise.all([api.get("/contacts/filters"), api.get("/contact-lists")]);
    setFilters(f.data);
    setLists(l.data);
    api.get("/contacts/duplicates")
      .then(({ data }) => setDupCount(data.count))
      .catch((err) => console.error("Could not scan for duplicates", err));
    api.get("/contact-submissions", { params: { status: "pending" } })
      .then(({ data }) => setPendingCount(data.length))
      .catch((err) => console.error("Could not load sign-up requests", err));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const people = useMemo(
    () => contacts.filter((c) => c.kind === "person").map((c) => ({ id: c.id, name: c.full_name })),
    [contacts]
  );

  const savePerson = async (form, { companyIds, newCompanies }) => {
    try {
      let personId = editingPerson?.id;
      if (personId) await api.put(`/people/${personId}`, form);
      else personId = (await api.post("/people", form)).data.id;

      const existing = editingPerson?.company_ids || [];
      for (const cid of companyIds.filter((id) => !existing.includes(id))) {
        await api.post("/contacts/link", { person_id: personId, company_id: cid });
      }
      for (const cid of existing.filter((id) => !companyIds.includes(id))) {
        await api.post("/contacts/unlink", { person_id: personId, company_id: cid });
      }
      for (const name of newCompanies) {
        await api.post("/companies/with-contact", {
          company: { name, type: "client" }, contact_person_id: personId,
        });
      }
      toast.success("Contact saved");
      setPersonOpen(false); setEditingPerson(null);
      await Promise.all([load(), loadMeta()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save that contact");
    }
  };

  const saveCompany = async (form, contactInfo) => {
    try {
      if (editingCompany?.id) await api.put(`/companies/${editingCompany.id}`, form);
      else await api.post("/companies/with-contact", { company: form, ...contactInfo });
      toast.success("Company saved");
      setCompanyOpen(false); setEditingCompany(null);
      await Promise.all([load(), loadMeta()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save that company");
    }
  };

  const onEdit = (contact) => {
    setDetail(null);
    if (contact.kind === "person") { setEditingPerson(contact.record); setPersonOpen(true); }
    else { setEditingCompany(contact.record); setCompanyOpen(true); }
  };

  const onDelete = async (contact) => {
    const what = contact.kind === "person" ? "contact" : "company";
    if (!window.confirm(`Delete this ${what}? This can't be undone.`)) return;
    try {
      await api.delete(contact.kind === "person" ? `/people/${contact.id}` : `/companies/${contact.id}`);
      toast.success("Deleted");
      await Promise.all([load(), loadMeta()]);
    } catch { toast.error("Could not delete that"); }
  };

  const onToggleList = async (contact, list, add) => {
    try {
      await api.post(`/contact-lists/${list.id}/members`, {
        [contact.kind === "person" ? "person_ids" : "company_ids"]: [contact.id],
        action: add ? "add" : "remove",
      });
      toast.success(add ? `Added to ${list.name}` : `Removed from ${list.name}`);
      await Promise.all([load(), loadMeta()]);
    } catch { toast.error("Could not update that list"); }
  };

  const openPersonById = (id) => {
    setDetail(null);
    const hit = contacts.find((c) => c.kind === "person" && c.id === id);
    if (hit) { setDetail(hit); return; }
    api.get(`/people/${id}`).then(({ data }) => { setEditingPerson(data); setPersonOpen(true); });
  };

  const openCompanyById = (id) => {
    setDetail(null);
    const hit = contacts.find((c) => c.kind === "company" && c.id === id);
    if (hit) { setDetail(hit); return; }
    api.get(`/companies/${id}`).then(({ data }) => { setEditingCompany(data); setCompanyOpen(true); });
  };

  const activeFilters = [role, tag, listId, companyId].filter((v) => v !== ALL).length;
  const clearFilters = () => { setRole(ALL); setTag(ALL); setListId(ALL); setCompanyId(ALL); setQ(""); };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Everyone and every organisation you work with — {counts.people} {counts.people === 1 ? "person" : "people"}, {counts.companies} {counts.companies === 1 ? "company" : "companies"}.
          </p>
        </div>
        <div className="md:ml-auto flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="contacts-search" placeholder="Search names, emails, roles, tags…"
                   className="pl-9 w-72" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <ShareIntakeLink />
          <Dialog open={personOpen} onOpenChange={(v) => { setPersonOpen(v); if (!v) setEditingPerson(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" onClick={() => setEditingPerson(null)}>
                <Plus className="w-4 h-4 mr-2" /> Add contact
              </Button>
            </DialogTrigger>
            <PersonDialog person={editingPerson} companies={filters.companies} onSave={savePerson} />
          </Dialog>
          <Dialog open={companyOpen} onOpenChange={(v) => { setCompanyOpen(v); if (!v) setEditingCompany(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-company-btn" onClick={() => setEditingCompany(null)}>
                <Building2 className="w-4 h-4 mr-2" /> Add company
              </Button>
            </DialogTrigger>
            <CompanyDialog company={editingCompany} people={people} onSave={saveCompany} />
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="contacts-tab-all" value="all">All</TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-people" value="people">People</TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-companies" value="companies">Companies</TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-lists" value="lists">Lists</TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-duplicates" value="duplicates">
            Duplicates{dupCount > 0 ? ` (${dupCount})` : ""}
          </TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-requests" value="requests">
            Requests{pendingCount > 0 ? ` (${pendingCount})` : ""}
          </TabsTrigger>
          <TabsTrigger data-testid="contacts-tab-demos" value="demos">Demo enquiries</TabsTrigger>
        </TabsList>

        {["all", "people", "companies"].map((key) => (
          <TabsContent key={key} value={key} className="space-y-4 pt-2">
            <div className="flex flex-wrap items-end gap-3">
              <FilterSelect label="Role / type" value={role} onChange={setRole}
                            testId="contacts-filter-role" options={filters.roles} allLabel="Any role" />
              <FilterSelect label="Tag" value={tag} onChange={setTag}
                            testId="contacts-filter-tag" options={filters.tags} allLabel="Any tag" />
              <FilterSelect label="List" value={listId} onChange={setListId}
                            testId="contacts-filter-list" allLabel="Any list"
                            options={lists.map((l) => ({ value: l.id, label: l.name }))} />
              <FilterSelect label="Company" value={companyId} onChange={setCompanyId}
                            testId="contacts-filter-company" allLabel="Any company"
                            options={filters.companies.map((c) => ({ value: c.id, label: c.name }))} />
              {(activeFilters > 0 || q) && (
                <Button variant="ghost" size="sm" data-testid="contacts-clear-filters" onClick={clearFilters}>
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>

            {contacts.length === 0 ? (
              <Card className="studio-card p-10 text-center" data-testid="contacts-empty">
                <Users className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-lg font-medium">Nothing here yet</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Add a contact, or import a list from Import / Export.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                {contacts.map((c) => (
                  <ContactCard key={`${c.kind}-${c.id}`} contact={c} lists={lists}
                               onExpand={setDetail} onEdit={onEdit} onDelete={onDelete}
                               onToggleList={onToggleList} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="lists" className="pt-2">
          <ListsPanel lists={lists} contacts={contacts} onChanged={() => Promise.all([load(), loadMeta()])}
                      onFilterList={(id) => { setListId(id); setTab("all"); }} />
        </TabsContent>

        <TabsContent value="duplicates" className="pt-2">
          <DuplicatesPanel onChanged={() => Promise.all([load(), loadMeta()])} />
        </TabsContent>

        <TabsContent value="requests" className="pt-2">
          <SubmissionsPanel onChanged={() => Promise.all([load(), loadMeta()])} />
        </TabsContent>

        <TabsContent value="demos" className="pt-2">
          <DemoInboxPanel />
        </TabsContent>
      </Tabs>

      <ContactDetailDialog contact={detail} open={!!detail}
                           onOpenChange={(v) => { if (!v) setDetail(null); }}
                           onEdit={onEdit} onOpenPerson={openPersonById} onOpenCompany={openCompanyById} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, allLabel, testId }) {
  const normalised = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  if (normalised.length === 0) return null;
  return (
    <div className="w-44">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {normalised.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ListsPanel({ lists, onChanged, onFilterList }) {
  const [name, setName] = useState("");
  const [allContacts, setAllContacts] = useState([]);

  useEffect(() => {
    api.get("/contacts").then(({ data }) => setAllContacts(data.contacts))
      .catch((err) => console.error("Could not load contacts for lists", err));
  }, [lists]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/contact-lists", { name: name.trim() });
      setName(""); toast.success("List created"); await onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create that list"); }
  };

  const rename = async (list) => {
    const next = window.prompt("Rename this list", list.name);
    if (!next || next === list.name) return;
    try { await api.put(`/contact-lists/${list.id}`, { name: next }); await onChanged(); }
    catch { toast.error("Could not rename that list"); }
  };

  const remove = async (list) => {
    if (!window.confirm(`Delete the list "${list.name}"? The contacts themselves stay.`)) return;
    try { await api.delete(`/contact-lists/${list.id}`); toast.success("List deleted"); await onChanged(); }
    catch { toast.error("Could not delete that list"); }
  };

  const addMember = async (list, contact) => {
    try {
      await api.post(`/contact-lists/${list.id}/members`, {
        [contact.kind === "person" ? "person_ids" : "company_ids"]: [contact.id], action: "add",
      });
      await onChanged();
    } catch { toast.error("Could not add that contact"); }
  };

  const removeMember = async (list, kind, id) => {
    try {
      await api.post(`/contact-lists/${list.id}/members`, {
        [kind === "person" ? "person_ids" : "company_ids"]: [id], action: "remove",
      });
      await onChanged();
    } catch { toast.error("Could not remove that contact"); }
  };

  const nameFor = (kind, id) =>
    allContacts.find((c) => c.kind === kind && c.id === id)?.name || "Unknown";

  return (
    <div className="space-y-4" data-testid="contact-lists-panel">
      <Card className="studio-card p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">New list</Label>
          <Input data-testid="list-name-input" value={name} placeholder="e.g. The Pack, Actors, Crew, Investors"
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
        </div>
        <Button data-testid="list-create-btn" onClick={create} disabled={!name.trim()}>
          <Plus className="w-4 h-4 mr-2" /> Create list
        </Button>
      </Card>

      {lists.length === 0 ? (
        <Card className="studio-card p-10 text-center" data-testid="lists-empty">
          <ListPlus className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-lg font-medium">No lists yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Group contacts however you work — a list can hold both people and companies.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {lists.map((l) => {
            const members = [
              ...(l.person_ids || []).map((id) => ({ kind: "person", id })),
              ...(l.company_ids || []).map((id) => ({ kind: "company", id })),
            ];
            const candidates = allContacts.filter(
              (c) => !(c.kind === "person" ? l.person_ids : l.company_ids || []).includes(c.id)
            );
            return (
              <Card key={l.id} className="studio-card p-5" data-testid={`list-card-${l.id}`}>
                <div className="flex items-center gap-2">
                  <button type="button" className="font-medium hover:underline"
                          data-testid={`list-open-${l.id}`} onClick={() => onFilterList(l.id)}>
                    {l.name}
                  </button>
                  <Badge variant="secondary" className="text-[10px]">{members.length}</Badge>
                  <div className="ml-auto flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`list-add-member-${l.id}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                        {candidates.length === 0 && <DropdownMenuItem disabled>Everyone's in</DropdownMenuItem>}
                        {candidates.slice(0, 100).map((c) => (
                          <DropdownMenuItem key={`${c.kind}-${c.id}`} onClick={() => addMember(l, c)}>
                            {c.kind === "company" ? "🏢 " : ""}{c.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="ghost" size="sm" data-testid={`list-rename-${l.id}`} onClick={() => rename(l)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`list-delete-${l.id}`} onClick={() => remove(l)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {members.length === 0 && <span className="text-xs text-muted-foreground">Nobody in this list yet.</span>}
                  {members.map((m) => (
                    <Badge key={`${m.kind}-${m.id}`} variant="outline" className="gap-1 font-normal">
                      {nameFor(m.kind, m.id)}
                      <button type="button" data-testid={`list-remove-member-${l.id}-${m.id}`}
                              onClick={() => removeMember(l, m.kind, m.id)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
